import { useCallback, useEffect, useReducer } from 'react'
import effects from 'app/effects/effects'
import * as R from 'ramda'
import moment from 'moment'

export const SUBSCRIBERS_PAGE_SIZE = 10000
export const FIRST_PAGE = 1
export const EventActionType = { GOING: 1, MAYBE: 2, NOT_GOING: 3 }

const initialState = {
  notificationId: undefined,
  eventAction: undefined,
  currentPage: FIRST_PAGE,
  hasMore: false,
  data: {},
  isLoading: false,
  isRefetching: false,
  error: undefined,
  fetchedAt: '',
  orderDataIds: [],
  totalCount: undefined,
}

export const EventSubscribersActionType = {
  INIT: 'INIT',
  SET_NOTIFICATION_ACTION: 'SET_NOTIFICATION_ACTION',
  INCREMENT_PAGE: 'INCREMENT_PAGE',
  REFETCH: 'REFETCH',
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FINAL: 'FETCH_FINAL',
  FETCH_FAILURE: 'FETCH_FAILURE',
}

const mapToObjWithKey = (key) =>
  R.reduce((acc, content) => ({ ...acc, [key(content)]: content }), {})

const parseContent = (state, action) => {
  const actionMapObj = R.compose(
    mapToObjWithKey(R.path(['subscriber', 'id'])),
    R.prop('payload'),
  )(action)
  const actionIds = R.compose(
    R.reduce(
      (acc, content) => [...acc, R.path(['subscriber', 'id'], content)],
      [],
    ),
    R.prop('payload'),
  )(action)

  if (state.isRefetching === true) {
    return {
      orderDataIds: actionIds,
      data: actionMapObj,
      totalCount: R.prop('totalCount', action),
    }
  } else {
    return {
      orderDataIds: state.orderDataIds.concat(actionIds),
      data: { ...state.data, ...actionMapObj },
      totalCount: R.prop('totalCount', action),
    }
  }
}

const eventActionsReducer = (state, action) => {
  switch (action.type) {
    case EventSubscribersActionType.INIT: {
      return initialState
    }
    case EventSubscribersActionType.SET_NOTIFICATION_ACTION: {
      return {
        ...initialState,
        hasMore: true,
        notificationId: action.payload.notificationId,
        eventAction: action.payload.action,
      }
    }
    case EventSubscribersActionType.REFETCH:
      return {
        ...state,
        fetchedAt: moment(),
        currentPage: FIRST_PAGE,
        hasMore: true,
        isRefetching: true,
      }
    case EventSubscribersActionType.REFETCH_PAGE:
      return {
        ...state,
        fetchedAt: moment(),
        hasMore: true,
        currentPage: action.payload,
      }
    case EventSubscribersActionType.INCREMENT_PAGE:
      return {
        ...state,
        currentPage: R.inc(state.currentPage),
      }
    case EventSubscribersActionType.FETCH_INIT:
      return {
        ...state,
        isLoading: true,
        error: undefined,
      }
    case EventSubscribersActionType.FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isRefetching: false,
        error: undefined,
        ...parseContent(state, action),
      }
    case EventSubscribersActionType.FETCH_FINAL:
      return {
        ...state,
        isLoading: false,
        hasMore: false,
        error: undefined,
        isRefetching: false,
        ...parseContent(state, action),
      }
    case EventSubscribersActionType.FETCH_FAILURE:
      return {
        ...state,
        isLoading: false,
        hasMore: false,
        isRefetching: false,
        error: action.payload,
      }
    default:
      throw new Error('Not supported action')
  }
}

export const useEventActionsSubscribers = () => {
  const [state, dispatchEventActions] = useReducer(
    eventActionsReducer,
    initialState,
  )

  const apiFetchersCallback = useCallback(() => {
    effects.apis
      .getNotiEventSubscribers({
        notificationId: state.notificationId,
        action: state.eventAction,
        page: state.currentPage,
        limit: SUBSCRIBERS_PAGE_SIZE,
      })
      .then(({ data: newSubscribers, meta }) => {
        const isLastPage = R.converge(R.equals, [
          R.prop('lastPage'),
          R.prop('currentPage'),
        ])
        if (isLastPage(meta) || !R.prop('lastPage', meta)) {
          dispatchEventActions({
            type: EventSubscribersActionType.FETCH_FINAL,
            payload: newSubscribers,
            totalCount: R.propOr(0, 'total', meta),
          })
        } else {
          dispatchEventActions({
            type: EventSubscribersActionType.FETCH_SUCCESS,
            payload: newSubscribers,
            totalCount: R.propOr(0, 'total', meta),
          })
        }
      })
      .catch((err) => {
        dispatchEventActions({
          type: EventSubscribersActionType.FETCH_FAILURE,
          payload: err,
        })
      })
  }, [state.currentPage, state.notificationId, state.eventAction])

  const refetchEventActions = () => {
    dispatchEventActions({ type: EventSubscribersActionType.REFETCH })
  }

  useEffect(() => {
    if (state.hasMore) {
      dispatchEventActions({ type: EventSubscribersActionType.FETCH_INIT })
      apiFetchersCallback()
    }
  }, [apiFetchersCallback, state.hasMore, state.fetchedAt])

  const loadMore = () => {
    dispatchEventActions({ type: EventSubscribersActionType.INCREMENT_PAGE })
  }

  const fetchCurrentPage = () => {
    dispatchEventActions({
      type: EventSubscribersActionType.REFETCH_PAGE,
      payload: state.currentPage,
    })
  }

  const setNotificationAction = (notificationId, action) => {
    if (
      notificationId !== state.notificationId ||
      (notificationId === state.notificationId && action !== state.eventAction)
    ) {
      dispatchEventActions({
        type: EventSubscribersActionType.SET_NOTIFICATION_ACTION,
        payload: { notificationId, action },
      })
    }
  }

  const backtoInitState = () => {
    dispatchEventActions({
      type: EventSubscribersActionType.INIT,
    })
  }

  return [
    {
      state,
      loadMore,
      EventSubscribersActionType,
      refetchEventActions,
      fetchCurrentPage,
      setNotificationAction,
      backtoInitState,
    },
    dispatchEventActions,
  ]
}
